import type { PostEmbedView } from '../components/PostMedia'

/** Bluesky moderation/self-label values that mark a post as adult. */
export const ADULT_LABELS: ReadonlySet<string> = new Set([
  'porn',
  'sexual',
  'sexual-content',
  'sexually-explicit',
  'adult',
  'nsfw',
  'nudity',
  'underwear',
  'graphic-media',
])

/** Hashtags (case-insensitive, no `#`) that flag a post as adult. */
export const ADULT_TAGS: ReadonlySet<string> = new Set([
  'nsfw',
  '18',
  '18plus',
  'r18',
  'r-18',
  'lewd',
  'horny',
  'kink',
  'fetish',
  'bdsm',
  'porn',
  'sex',
  'sexy',
  'erotic',
  'nude',
  'nudes',
  'topless',
  'lingerie',
  'hentai',
  'xxx',
  'onlyfans',
])

const HASHTAG_RE = /(?:^|\s|[(\[])#([\p{L}\p{N}_-]{2,40})/gu

export interface MediaThumb {
  src: string
  alt?: string
  isVideo?: boolean
  playlistUrl?: string
  fullsize?: string
}

/** Walk an embed and return every image / video thumb / external thumb. */
export function allImagesOf(embed: PostEmbedView | undefined): MediaThumb[] {
  if (!embed) return []
  const out: MediaThumb[] = []
  walk(embed as Record<string, unknown>, out)
  return out
}

function walk(shape: Record<string, unknown>, out: MediaThumb[]): void {
  const images =
    (shape.images as { thumb?: string; fullsize?: string; alt?: string }[] | undefined) ?? []
  for (const im of images) {
    const src = im.thumb ?? im.fullsize
    if (src) out.push({ src, alt: im.alt, fullsize: im.fullsize })
  }
  const video = shape as { thumbnail?: string; playlist?: string; alt?: string }
  if (typeof video.thumbnail === 'string' && video.thumbnail) {
    out.push({
      src: video.thumbnail,
      alt: video.alt,
      isVideo: true,
      playlistUrl: typeof video.playlist === 'string' ? video.playlist : undefined,
    })
  }
  const external = shape.external as
    | { thumb?: string; uri?: string; title?: string }
    | undefined
  if (external?.thumb) {
    const playable =
      typeof external.uri === 'string' && /\.(gif|mp4|webm)(\?|$)/i.test(external.uri)
        ? external.uri
        : undefined
    out.push({ src: external.thumb, alt: external.title, fullsize: playable })
  }
  const media = shape.media as Record<string, unknown> | undefined
  if (media) walk(media, out)
  const record = shape.record as
    | {
        embeds?: Record<string, unknown>[]
        embed?: Record<string, unknown>
      }
    | undefined
  if (record) {
    if (Array.isArray(record.embeds)) {
      for (const re of record.embeds) walk(re, out)
    }
    if (record.embed) walk(record.embed, out)
  }
}

export function hasAdultTag(text: string | undefined | null): boolean {
  if (!text) return false
  const re = new RegExp(HASHTAG_RE.source, 'giu')
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) != null) {
    if (ADULT_TAGS.has(m[1].toLowerCase())) return true
  }
  return false
}

export function isAdultPost(p: {
  record?: {
    labels?: { values?: { val: string }[] }
    text?: string
  }
  labels?: { val: string }[]
  text?: string
}): boolean {
  for (const l of p.labels ?? []) {
    if (ADULT_LABELS.has(l.val)) return true
  }
  for (const v of p.record?.labels?.values ?? []) {
    if (ADULT_LABELS.has(v.val)) return true
  }
  if (hasAdultTag(p.record?.text)) return true
  if (hasAdultTag(p.text)) return true
  return false
}
